/**
 * WHAT EACH NOTIFICATION IS CALLED ON SCREEN - the feed's rows and the preference matrix's rows.
 *
 * THE WORDS ARE THE APP'S OWN, BECAUSE THE PLATFORM SENDS NONE. `NotificationPreferences.items.label`
 * is declared and not emitted, and deliberately so: `notification_category` has no label column
 * (`src/modules/notifications/services/notification-preference.resolver.ts`, "`label` IS NOT
 * EMITTED, BECAUSE NO COLUMN HOLDS IT"), and the feed rows carry only `categoryCode`. Wording is the
 * app's business (decision-log rows 99 and 117), so the nineteen author-audience codes the catalogue
 * seeds (`0003_lookups_and_seeds`; `0097`'s three and `0102`'s one at the foot) are named
 * here once - in the catalogue, `notifications.category.<code>` (one for a disable and a re-enable
 * of an account, D42: the e-mail's wording says which). A `label` the platform does send wins; a
 * code the catalogue does not know is shown in readable form rather than dropped, so a twentieth
 * category is never invisible.
 */
import { i18n } from '../../i18n';
import type { ENGLISH_RESOURCES } from '../../i18n';

type CategoryCode = keyof (typeof ENGLISH_RESOURCES)['engagement']['notifications']['category'];

function isNamedCategory(code: string): code is CategoryCode {
  return code !== '' && i18n.exists(`engagement:notifications.category.${code}`);
}

/** `payout_statement` -> "Payout statement": a code nobody named still reads as words. */
function readable(code: string): string {
  const spaced = code.replaceAll('_', ' ').trim();
  return spaced === ''
    ? i18n.t('engagement:notifications.categoryFallback')
    : `${spaced.charAt(0).toUpperCase()}${spaced.slice(1)}`;
}

export function categoryWords(code: string, label?: string | null): string {
  if (label !== undefined && label !== null && label.trim() !== '') {
    return label;
  }
  return isNamedCategory(code)
    ? i18n.t(`engagement:notifications.category.${code}`)
    : readable(code);
}

/**
 * The row's `state`, for every state but one. `suppressed` is not here, because one word cannot be
 * right for it - see `stateWords` below.
 */
type WordedState = 'queued' | 'dispatching' | 'sent' | 'dead';
const WORDED_STATES: ReadonlySet<string> = new Set<WordedState>([
  'queued',
  'dispatching',
  'sent',
  'dead',
]);

function isWordedState(state: string): state is WordedState {
  return WORDED_STATES.has(state);
}

/**
 * ★ THE THREE THINGS A `suppressed` ROW CAN SAY (design 14, F9).
 *
 * `suppressed` is the dispatcher's ending for "nothing went out", and it has THREE causes, not one
 * (`src/modules/notifications/worker/notification-dispatch.service.ts`, the `no_channel`,
 * `no_target` and `push_unavailable` arms): the author's own switches left no channel on; or a
 * channel was on and there was nothing to send it to - no app on a phone, no contact e-mail on the
 * profile; or a phone was known and this server could not reach it. The feed row carries the state
 * and not the cause, so the words are derived from the one fact the cabinet CAN read: the author's
 * switches, `getNotificationPreferences`.
 *
 * Until 2026-10-01 every `suppressed` row said "you switched these off". The cabinet walk that day
 * found it on "Your application was approved" - a category with NO switch, listed on the very
 * preferences page as "Always sent" - for a person who had switched nothing off: they had no app
 * and no contact e-mail, so the message had nowhere to go. Blaming the author for a switch that does
 * not exist sent them hunting for it, and hid the real gap (the missing address).
 */
export const SUPPRESSED_WORDS = {
  /** A category the author can switch, with every channel it offers switched off by them. */
  switchedOff: 'notifications.suppressed.switchedOff',
  /** Everything else that was suppressed: on, or always on, and there was no address to use. */
  noAddress: 'notifications.suppressed.noAddress',
  /**
   * The switches could not be read (the preferences read failed). Neither of the two claims above
   * can be made without them, so the row says only what the state itself proves.
   */
  unknown: 'notifications.suppressed.unknown',
} as const;

/** The subset of a preference-matrix item (`NotificationPreferences.items[]`) these words read. */
export interface PreferenceFacts {
  readonly categoryCode: string;
  readonly isAlwaysOn: boolean;
  readonly channels: readonly { readonly enabled: boolean }[];
}

/**
 * "The author switched this category off": a SWITCHABLE category (`isAlwaysOn` false - the matrix's
 * own answer, never a list typed here) whose every offered channel is off. A category the matrix
 * does not list, an always-on one, or one with a channel still on was not stopped by a switch.
 *
 * ★ EVERY CHANNEL, NOT ANY. A category with one channel off and another on that still came out
 * `suppressed` was stopped on the channel that was ON - by a missing address - so "you switched
 * these off" would blame the wrong thing. (Today's two switchable categories offer the phone only,
 * so the two readings agree; the stricter one is the one that stays true when that changes.)
 *
 * The switches are read NOW, and the row was decided when it was sent: an author who switched tips
 * back on since is told the later truth about an earlier row. That is the one fact the feed can
 * offer - the row carries no cause - and it is right for every row sent since the last change.
 */
export function switchedOffByAuthor(
  categoryCode: string,
  preferences: readonly PreferenceFacts[],
): boolean {
  const item = preferences.find((candidate) => candidate.categoryCode === categoryCode);
  return (
    item !== undefined &&
    !item.isAlwaysOn &&
    item.channels.length > 0 &&
    item.channels.every((channel) => !channel.enabled)
  );
}

/**
 * The words for a row's `state`. `preferences` is the author's matrix, or `undefined` when it could
 * not be read; only a `suppressed` row consults it. An unknown state is shown as itself rather than
 * dropped, the rule `categoryWords` keeps for an unknown category.
 */
export function stateWords(
  state: string,
  categoryCode: string,
  preferences: readonly PreferenceFacts[] | undefined,
): string {
  if (state !== 'suppressed') {
    return isWordedState(state) ? i18n.t(`engagement:notifications.state.${state}`) : state;
  }
  const words =
    preferences === undefined
      ? SUPPRESSED_WORDS.unknown
      : switchedOffByAuthor(categoryCode, preferences)
        ? SUPPRESSED_WORDS.switchedOff
        : SUPPRESSED_WORDS.noAddress;
  return i18n.t(words, { ns: 'engagement' });
}

/** A channel as a column and a switch name it: "In the app", "E-mail". */
export function channelWords(channel: 'push' | 'email'): string {
  return i18n.t(`engagement:notifications.channel.${channel}`);
}

/** The subset of a feed row's `deliveries[]` entry these words read. */
export interface DeliveryFacts {
  readonly channel: 'push' | 'email';
  readonly outcome: string;
  readonly emailEvent?: string | null;
}

/** The e-mail provider's events, each to its words in the catalogue (`notifications.emailEvent`). */
const EMAIL_EVENT_WORDS = {
  'email.sent': 'sent',
  'email.delivered': 'delivered',
  'email.delivery_delayed': 'delayed',
  'email.bounced': 'bounced',
  'email.complained': 'complained',
  'email.failed': 'failed',
} as const;

type EmailEvent = keyof typeof EMAIL_EVENT_WORDS;

function isEmailEvent(event: string): event is EmailEvent {
  return Object.hasOwn(EMAIL_EVENT_WORDS, event);
}

/** What became of an e-mail; an event this table does not know reads as "sent", as before. */
function emailOutcome(event: string | null): string {
  const words = event !== null && isEmailEvent(event) ? EMAIL_EVENT_WORDS[event] : 'sent';
  return i18n.t(`engagement:notifications.emailEvent.${words}`);
}

/**
 * One channel attempt in plain words. A PUSH NEVER SAYS "DELIVERED": it tops out at `accepted`, the
 * message handed to the phone's notification service, because per-message push delivery data does
 * not exist (authors-api.yaml, `listNotifications`). Only e-mail has a real outcome to report.
 */
export function deliveryWords(delivery: DeliveryFacts): string {
  const channel = channelWords(delivery.channel);
  if (delivery.outcome !== 'accepted') {
    return i18n.t('engagement:notifications.delivery.notSent', { channel });
  }
  if (delivery.channel === 'push') {
    return i18n.t('engagement:notifications.delivery.handedToPhone', { channel });
  }
  return i18n.t('engagement:notifications.delivery.email', {
    channel,
    outcome: emailOutcome(delivery.emailEvent ?? null),
  });
}

/**
 * How the always-on list's "Sent" column names each channel: "in the app and by e-mail". A push is a
 * notification through the MoonX app on the author's phone; "by phone" read oddly (design 24, T18:
 * the owner's "In the app", 2026-10-11; e-mail left as it is).
 */
export function channelSentWords(channel: 'push' | 'email'): string {
  return i18n.t(`engagement:notifications.channelSent.${channel}`);
}

/*
 * The Payment details notices: the three of design 24 B2-REMIND (`0097`) - the reminders 30 and 7
 * days before the date Payment details must be complete by, and a payout a run held because they
 * were not - and design 28's notice that Payment details were changed (C17, `0102`). All four are
 * always sent, so the preferences page lists them under "always sent". They are named in the
 * catalogue with the other categories.
 */
